import { DragEvent, FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { UploadIcon } from 'components/icons/Icons';
import { AlertCircle } from 'lucide-react';

interface DropZoneProps {
  isDragOver: boolean;
  errorMessage: string | null;
  maxFileSizeMb: number;
  onBrowse: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
}

export const DropZone: FC<DropZoneProps> = ({
  isDragOver,
  errorMessage,
  maxFileSizeMb,
  onBrowse,
  onDrop,
  onDragOver,
  onDragLeave
}) => {
  const { t } = useTranslation(NsKey.IdentityUpload);

  return (
    <div className="flex flex-col gap-4">
      <div
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-primary hover:bg-gray-50'
        }`}
        onClick={onBrowse}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-base font-medium text-gray-700 mb-1">
          {t('IDENTITY_UPLOAD.DIALOG.UPLOAD_ZONE_TITLE')}
        </p>
        <p className="text-sm text-gray-500 mb-4">
          {t('IDENTITY_UPLOAD.DIALOG.UPLOAD_ZONE_HINT')}
        </p>
        <Button
          color="primary"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onBrowse();
          }}
        >
          <UploadIcon className="w-4 h-4 mr-2" />
          {t('IDENTITY_UPLOAD.DIALOG.UPLOAD_ZONE_CHOOSE')}
        </Button>
      </div>
      <p className="text-xs text-gray-400 text-center">
        {t('IDENTITY_UPLOAD.DIALOG.UPLOAD_ZONE_FORMATS', { sizeMb: maxFileSizeMb })}
        <br />
        {t('IDENTITY_UPLOAD.DIALOG.UPLOAD_ZONE_AUTO_VERIFY_HINT')}
      </p>
      {errorMessage && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-sm text-red-700"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};
